/**
 * Screen 3 of 4 — VALUE & PROVENANCE.
 *
 * Shows the band and then shows its working. Two rules govern this screen:
 *
 *   1. The deliverable is a BAND, never a bare point estimate. The engine's
 *      `point_estimate` appears only inside the triangulation panel, as an
 *      input to the band — never as the headline number.
 *   2. `positioning` is rendered verbatim. It is the AICPA SSVS No. 1 sentence
 *      that distinguishes a calculated value from a conclusion of value; it is
 *      not ours to paraphrase into something friendlier.
 *
 * VAL-PATH-P6a: the screen runs the SAVED draft on entry (recompute, no body —
 * a preview, never a write) so the value and its provenance always match what
 * the earlier steps saved. A session copy of an earlier run went stale the
 * moment an auto-apply changed the draft; it is no longer read here. If the
 * run cannot happen, the screen says so and offers to run it again.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { getValuationEngagement } from '@/api/valuation'
import { recomputeValuation } from '@/api/valuationB3'
import { formatApiError } from '@/utils/httpErrors'
import { useAuth } from '@/contexts/AuthContext'
import type { ValuationEngagement } from '@/types/valuation'
import type { RecomputeResponse } from '@/types/valuationB3'
import {
  StatusBadge,
  basisLabel,
  ValuationStepper,
  ValueKindBadge,
} from './_shared'
import { stepRole } from './_steps'
import { useValuationDraft } from './_valuationDraft'
import { MarketOnlyAck, isMarketOnlyRun } from './_MarketOnlyAck'
import { ValueMethodsSection } from './_ValueMethodsSection'
import type { StepSaveResult } from './_stepContext'
import { money } from '@/utils/format'
import { nextStep } from './_nextStep'
import { StepFooter } from '@/components/valuation/StepFooter'
import { convergenceLabel } from './_convergenceLabel'
import { isPaymentRequired, valueBlockerFromError, type ValueBlocker } from '@/api/valuationPayment'
import { PayForValue, type CheckoutReturn } from './_PayForValue'
import { CLIENT_TEXT as BLOCKED_TEXT, PaidOn, ValueBlocked } from './_ValueBlocked'
import { isDisclaimerRequiredError } from '@/api/valuationDisclaimer'
import { DisclaimerAccept } from './_DisclaimerAccept'
import { TierIncludes } from './_TierIncludes'
import { AddOptions } from './_AddOptions'
import { OptionValue } from './_OptionValue'
import {
  ApproachesPanel,
  BenchmarkPanel,
  ComparableEvidencePanel,
  EarningsAndRatePanel,
  OverridesPanel,
  TriangulationPanel,
} from './_OutputProvenanceSigned'

// The server's refusal when no draft is saved (apps/api/routers/_valuation_saved_inputs.py).
const SERVER_NOTHING_SAVED = 'Nothing is saved for this engagement yet.'

function isNothingSaved(err: unknown): boolean {
  const data = (err as { response?: { data?: { error?: { message?: string }; detail?: unknown } } })
    ?.response?.data
  return data?.error?.message === SERVER_NOTHING_SAVED || data?.detail === SERVER_NOTHING_SAVED
}

export default function ValuationOutput() {
  const { valuationId } = useParams<{ valuationId: string }>()
  const { user } = useAuth()
  const isOwner = stepRole(user?.role) === 'owner'
  const ownerRef = useRef(isOwner)
  ownerRef.current = isOwner
  const { draft, draftExtras, updatedAt, save } = useValuationDraft(valuationId)

  const [engagement, setEngagement] = useState<ValuationEngagement | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [run, setRun] = useState<RecomputeResponse | null>(null)
  const [running, setRunning] = useState(true)
  const [runError, setRunError] = useState<string | null>(null)
  const [nothingSaved, setNothingSaved] = useState(false)
  // VAL-STRIPE-GATE-1: 402 = an owner who has not paid; the pay screen replaces the value.
  const [payRequired, setPayRequired] = useState(false)
  const [searchParams] = useSearchParams()
  const checkoutParam = searchParams.get('checkout')
  const returned: CheckoutReturn = checkoutParam === 'done' || checkoutParam === 'cancelled' ? checkoutParam : null
  const [polls, setPolls] = useState(0)
  // VAL-PAY-FLOW-1: the owner's order is estimate disclaimer -> pay -> value. The server
  // answers 409 `disclaimer_required` first, then 402 until the Stripe webhook records payment.
  const [disclaimerNeeded, setDisclaimerNeeded] = useState(false)
  // VAL-FIX-PAY-BUGS-1 bug 2: the value was refused for a reason a step fixes.
  const [blocked, setBlocked] = useState<ValueBlocker | null>(null)
  // A gate (disclaimer or payment) refused the value at least once: Questions skipped its
  // run too, so the income value it would have saved (D6) is saved when the value first shows.
  const gated = useRef(false)
  const [ack, setAck] = useState(false)
  const savedAck = draft?.market_only_acknowledged ?? false

  useEffect(() => { setAck(savedAck) }, [savedAck])

  useEffect(() => {
    if (!valuationId) return
    let live = true
    getValuationEngagement(valuationId)
      .then(en => { if (live) setEngagement(en) })
      .catch((err: unknown) => { if (live) setLoadError(formatApiError(err)) })
    return () => { live = false }
  }, [valuationId])

  // VAL-FIX-PAY-BUGS-1 bug 1: every answer from the server replaces the whole view - a
  // gate, a refusal or the value - so a state left over from an earlier answer (the pay
  // screen, the disclaimer, an error) can never sit beside, or hide, the current one.
  const showOnly = useCallback((next: {
    disclaimer?: boolean; pay?: boolean; blocker?: ValueBlocker | null; nothing?: boolean; error?: string | null
  }) => {
    setDisclaimerNeeded(!!next.disclaimer)
    setPayRequired(!!next.pay)
    setBlocked(next.blocker ?? null)
    setNothingSaved(!!next.nothing)
    setRunError(next.error ?? null)
  }, [])

  const runValue = useCallback((live: () => boolean) => {
    if (!valuationId) return
    setRunning(true)
    setRunError(null)
    recomputeValuation(valuationId)
      .then(res => {
        if (!live()) return
        setRun(res); showOnly({})
        if (gated.current) {
          gated.current = false
          if (res.income_value != null) void save({ income_value: res.income_value })
          // The status changed (accepted / paid) since this screen read the engagement:
          // read it again so the header and "Paid on" match without a page reload.
          getValuationEngagement(valuationId).then(en => { if (live()) setEngagement(en) }).catch(() => {})
        }
      })
      .catch((err: unknown) => {
        if (!live()) return
        const blocker = valueBlockerFromError(err)
        if (isDisclaimerRequiredError(err)) { gated.current = true; showOnly({ disclaimer: true }) }
        else if (isPaymentRequired(err)) { gated.current = true; showOnly({ pay: true }) }
        else if (isNothingSaved(err)) showOnly({ nothing: true })
        // Bug 2: a refusal that names a step shows plain words and a link to that step.
        else if (blocker) { setRun(null); showOnly({ blocker }) }
        // Never a status or a code to an owner (a client); a signer keeps the detail.
        else showOnly({ error: ownerRef.current ? BLOCKED_TEXT['blocked.retry'] : formatApiError(err) })
      })
      .finally(() => { if (live()) setRunning(false) })
  }, [valuationId, save, showOnly])

  useEffect(() => {
    let live = true
    runValue(() => live)
    return () => { live = false }
  }, [runValue])

  // Back from Stripe: the webhook may land a moment after the browser does. Ask again
  // every 3 s for a minute; the server, not the return URL, decides when it is paid.
  useEffect(() => {
    if (!payRequired || returned !== 'done' || polls >= 20) return
    const t = window.setTimeout(() => { setPolls(n => n + 1); runValue(() => true) }, 3000)
    return () => window.clearTimeout(t)
  }, [payRequired, returned, polls, runValue])

  const needsAck = isOwner && isMarketOnlyRun(run)

  const saveAck = async (): Promise<StepSaveResult> => {
    if (!needsAck || ack === savedAck) return { changed: false, savedAt: updatedAt }
    const err = await save({ market_only_acknowledged: ack })
    if (err) throw new Error(err)
    return { changed: true, savedAt: new Date().toISOString() }
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div className="rounded-lg border border-pal-error-border bg-pal-error-bg p-4 text-sm text-pal-error-text">{loadError}</div>
      </div>
    )
  }
  if (!engagement) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <div>
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-2 h-4 w-40" />
        </div>
        <Skeleton className="h-10 w-full" />
        <Card>
          <CardHeader><Skeleton className="h-5 w-40" /></CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-8 w-56" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader><Skeleton className="h-5 w-40" /></CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </CardContent>
        </Card>
      </div>
    )
  }

  const concluded = engagement.value_kind === 'concluded'
  const persistedLow = concluded ? engagement.concluded_value_low : engagement.calculated_value_low
  const persistedHigh = concluded ? engagement.concluded_value_high : engagement.calculated_value_high
  // The fresh run is the value. The stored band only stands in when the run
  // failed, under "Value out of date — recalculate".
  const low = run ? run.value_low : runError ? persistedLow : null
  const high = run ? run.value_high : runError ? persistedHigh : null
  const hasBand = low !== null && low !== undefined && high !== null && high !== undefined
  // R5: disabled until a value exists; the reason is the same nextStep()
  // answer the rest of this brief uses ("Continue intake"/"Start intake").
  const step = nextStep(engagement, user?.role)

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-pal-ink">{engagement.subject_company_name}</h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-pal-ink-muted">
          <ValueKindBadge kind={engagement.value_kind} owner={user?.role === 'valuation_owner'} />
          <StatusBadge status={engagement.status} />
          <span className="text-xs">{engagement.industry_label ?? engagement.industry ?? 'no industry set'}</span>
          {engagement.period_label && <span>· {engagement.period_label}</span>}
        </div>
      </div>

      <ValuationStepper current="output" engagementId={valuationId} />

      {runError && (
        <div className="space-y-3 rounded-lg border border-pal-error-border bg-pal-error-bg p-4 text-sm text-pal-error-text">
          <p>{runError}</p>
          <Button variant="outline" size="sm" disabled={running} onClick={() => runValue(() => true)}>
            Value out of date — recalculate
          </Button>
        </div>
      )}

      {disclaimerNeeded && valuationId ? (
        <DisclaimerAccept engagementId={valuationId} onAccepted={() => runValue(() => true)} />
      ) : payRequired && valuationId ? (
        <PayForValue engagementId={valuationId} returned={returned} slow={polls >= 20}
                     onDisclaimerRequired={() => showOnly({ disclaimer: true })}
                     onPaid={() => runValue(() => true)} />
      ) : blocked && valuationId ? (
        <ValueBlocked engagementId={valuationId} blocker={blocked}
                      paidAt={isOwner ? engagement.payment?.paid_at : null} />
      ) : running && !run ? (
        <Card>
          <CardHeader><Skeleton className="h-5 w-40" /></CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-8 w-56" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>
      ) : nothingSaved ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-pal-ink-soft">Nothing is saved yet — open Questions, then Your value.</p>
          </CardContent>
        </Card>
      ) : run?.owner_view ? (
        // VAL-68-OWNER-RULINGS R3: Value at a Glance / Value + Drivers - only what was bought.
        <OptionValue run={run} paidAt={isOwner ? engagement.payment?.paid_at : null} />
      ) : !hasBand ? null : (
        <Card>
          <CardHeader>
            {/* VAL-RELEASE-PAY-TOS-1: the owner's heading is the approved owner title
                (VAL-WORDING-APPLY-1); a signer keeps "Calculated value". */}
            <CardTitle>{concluded ? 'Conclusion of value' : isOwner ? "Owner's Estimated Value" : 'Calculated value'}</CardTitle>
            {/* VAL-FIX-PAY-BUGS-1 bug 3: the owner sees when the value was paid. */}
            {isOwner && <PaidOn paidAt={engagement.payment?.paid_at} />}
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold tracking-tight text-pal-navy">
              {money(low)} <span className="text-pal-ink-faint">–</span> {money(high)}
            </p>
            <p className="mt-3 text-sm text-pal-ink-body">
              {run?.positioning ?? (concluded
                ? 'Conclusion of value (AICPA SSVS No. 1 valuation engagement).'
                // VAL-WORDING-APPLY-1 H7 (owner 10-29): the owner's estimate in plain words.
                : isOwner
                  ? 'Your estimated value, calculated by the platform from your figures. It is not a signed valuation.'
                  : 'Calculated value (AICPA SSVS No. 1 calculation engagement) — NOT a conclusion of value.')}
            </p>
            {run && (
              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <span className="rounded bg-pal-surface-muted px-2 py-1 font-mono text-pal-ink-soft">
                  {basisLabel(run.computed_range.basis)}
                </span>
                <span
                  className={`rounded px-2 py-1 font-mono ${
                    run.convergence_status === 'converged'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-amber-100 text-amber-900'
                  }`}
                >
                  {convergenceLabel(run.convergence_status)}
                </span>
              </div>
            )}
            {run && (run.convergence_status.startsWith('divergent') || run.override_out_of_range || run.override_unresolved) && (
              <div className="mt-4 rounded-lg border border-pal-warning-border bg-pal-warning-bg p-3 text-sm text-pal-pill-warning-text">
                This run requires Valuation Signer review — the approaches did not converge inside the signed
                spread, or an override landed outside its signed band. It is not sign-off ready
                until a credentialed Valuation Signer has resolved that.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* VAL-TIERS-1-2: what the owner's purchase includes (owner/super_admin data only). */}
      {isOwner && run && !payRequired && <TierIncludes engagement={engagement} />}

      {/* VAL-68-OWNER-RULINGS R2: after buying, the higher options can be added. */}
      {isOwner && run && !payRequired && valuationId && <AddOptions engagementId={valuationId} />}

      {/* M-DEMO — Compare values + 10-year Forecast, below the headline. Absent
          when the switch is off (no value_methods), so the page is as before. */}
      {run?.value_methods && valuationId && (
        <ValueMethodsSection
          engagementId={valuationId}
          payload={run.value_methods}
          signer={!isOwner}
          stored={draftExtras.value_methods}
          onSaved={() => runValue(() => true)}
        />
      )}

      {needsAck && <MarketOnlyAck checked={ack} onChange={setAck} />}

      {run && !run.owner_view && (
        <>
          <BenchmarkPanel run={run} owner={isOwner} />
          <EarningsAndRatePanel run={run} owner={isOwner} />
          <ApproachesPanel run={run} owner={isOwner} />
          <TriangulationPanel run={run} />
          <ComparableEvidencePanel run={run} />
          <OverridesPanel run={run} />
        </>
      )}

      <StepFooter
        step="output"
        engagementId={valuationId ?? ''}
        role={user?.role}
        save={saveAck}
        nextDisabled={!run || (needsAck && !ack)}
        note={!hasBand && !run?.owner_view && !running ? `${step.label} first` : null}
      />
    </div>
  )
}
